<script>
import axios from 'axios'
import PasswordValidation from '@/src/components/password-validation.vue'
import { PRIVACY_POLICY_URL } from '@constants/links'

export default {
  page: {
    title: 'Reset Password',
    meta: [{ name: 'description', content: 'The Reset Password page.' }],
  },
  components: {
    PasswordValidation,
  },
  data() {
    return {
      uid: '',
      token: '',
      email: '',
      message: '',
      password: '',
      passwordConfirm: '',
      sendEmailError: null,
      confirmationError: false,
      tryingToSendEmail: false,
      tryingToResetPassword: false,
      passwordConfirmError: false,
      resetPasswordError: null,
      canSendEmail: true,
      tooltipShow: false,
    }
  },
  computed: {
    fromLink() {
      return this.uid !== '' && this.token !== ''
    },
    privacyPolicyUrl() {
      return PRIVACY_POLICY_URL
    },
  },
  mounted() {
    if (this.$route.params.uid && this.$route.params.token) {
      this.uid = this.$route.params.uid
      this.token = this.$route.params.token
    }
  },
  methods: {
    tryToSendEmail() {
      this.tryingToSendEmail = true
      axios
        .post('/api/v1/reset_password/', {
          email: this.email,
        })
        .then(() => {
          this.message = 'An email has been sent to your email account'
          this.canSendEmail = false
        })
        .catch((error) => {
          this.sendEmailError = error
        })
        .finally(() => {
          this.tryingToSendEmail = false
        })
    },
    tryToResetPassword() {
      this.tryingToResetPassword = true
      let canContinue = true
      if (this.password !== this.passwordConfirm) {
        this.passwordConfirmError = true
        this.tryingToResetPassword = false
        canContinue = false
      }

      if (canContinue) {
        axios
          .post('/api/v1/reset_password/confirm/', {
            uid: this.uid,
            token: this.token,
            password: this.password,
          })
          .then(() => this.$router.push({ name: 'login' }))
          .catch((error) => {
            this.resetPasswordError = error
          })
      }
    },
    clearPasswordErrors() {
      this.tooltipShow = true
      this.passwordConfirmError = null
      this.resetPasswordError = null
    },
    goBack() {
      this.$router.go(-1)
    },
  },
}
</script>

<template>
  <v-container fluid>
    <v-container :class="$style.innerContainer">
      <div v-if="confirmationError" class="text-danger">
        Something went wrong! Reset password link is expired or invalid.
      </div>
      <div v-else>
        <v-row
          no-gutters
          :class="$style.title"
          class="input--text"
          justify="center"
        >
          Account recovery
        </v-row>
        <div v-if="!fromLink">
          <!-- First page after forgot password link -->
          <!-- I am not entering through email link -->
          <v-row
            no-gutters
            :class="$style.subtitle"
            class="input--text"
            justify="center"
          >
            Enter your email and we will send you instructions on how to take it
            from there.
          </v-row>
          <v-row no-gutters justify="center">
            <v-text-field
              v-model="email"
              background-color="offwhite"
              :class="$style.email"
              autofocus
              name="email"
              placeholder="Email"
              rounded
              filled
              hide-details
              autocomplete="off"
              @keydown="sendEmailError = null"
            ></v-text-field>
          </v-row>
          <v-row class="mt-11" no-gutters justify="end">
            <v-btn
              width="112"
              height="56"
              rounded
              type="submit"
              color="primary"
              :class="$style.hrButton"
              class="my-auto text-capitalize"
              :loading="tryingToSendEmail"
              :disabled="!canSendEmail || sendEmailError || email.length === 0"
              @click="tryToSendEmail"
              >Next</v-btn
            >
          </v-row>
          <v-row v-if="sendEmailError" no-gutters justify="start">
            <p class="alert alert-danger mt-5" role="alert">
              {{ sendEmailError.response.data }}
            </p>
          </v-row>

          <v-row v-if="message" no-gutters justify="start">
            <p class="alert alert-success mt-5" role="alert">
              {{ message }}
            </p>
          </v-row>
        </div>
        <div v-else>
          <!-- I am entering through email link -->
          <v-row no-gutters justify="center">
            <v-text-field
              id="password"
              v-model="password"
              background-color="offwhite"
              :class="$style.email"
              autofocus
              name="password"
              type="password"
              placeholder="Enter your new password"
              rounded
              filled
              hide-details
              autocomplete="off"
              class="password_field"
              @blur="tooltipShow = false"
              @keydown="clearPasswordErrors"
            ></v-text-field>
          </v-row>

          <v-tooltip
            v-model="tooltipShow"
            color="black"
            :content-class="$style.passTooltip"
            attach=".password_field"
            :value="password"
          >
            <PasswordValidation :password="password"></PasswordValidation>
          </v-tooltip>

          <v-row v-if="passwordConfirmError" no-gutters justify="start">
            <p class="alert alert-danger mt-5" role="alert">
              Confirm password does not match
            </p>
          </v-row>

          <v-row class="mt-5" no-gutters justify="center">
            <v-text-field
              id="password_confirm"
              v-model="passwordConfirm"
              background-color="offwhite"
              :class="$style.email"
              autofocus
              name="password_confirm"
              type="password"
              placeholder="Confirm your password"
              rounded
              filled
              hide-details
              autocomplete="off"
              @keydown="passwordConfirmError = false"
            ></v-text-field>
          </v-row>

          <v-row class="mt-11" no-gutters justify="end">
            <v-btn
              width="112"
              height="56"
              rounded
              type="submit"
              color="primary"
              :class="$style.hrButton"
              class="my-auto text-capitalize"
              :loading="tryingToSendEmail"
              :disabled="passwordConfirmError || resetPasswordError"
              @click="tryToResetPassword"
              >Next</v-btn
            >
          </v-row>

          <v-row v-if="resetPasswordError" no-gutters justify="start">
            <p class="alert alert-danger mt-5" role="alert">
              {{ resetPasswordError.response.data }}
            </p>
          </v-row>

          <v-row v-if="message" no-gutters justify="start">
            <p class="alert alert-success mt-5" role="alert">
              {{ message }}
            </p>
          </v-row>
        </div>
      </div>
      <!-- terms row -->
      <v-row :class="$style.termsRow" align="center">
        <v-col cols="12" md="6" class="d-flex justify-start">
          <div
            v-if="!fromLink"
            class="pl-4 pt-2 clickable primary--text"
            :class="$style.hrRouterLinkText"
            @click="goBack"
            ><v-icon color="primary">mdi-chevron-left</v-icon> Back</div
          >
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-end">
          <a
            :href="privacyPolicyUrl"
            target="_blank"
            class="mr-5 input--text text-decoration-none"
            :class="$style.termsPrivacy"
            >Privacy</a
          >
          <a
            href="/terms.pdf"
            target="_blank"
            class="input--text text-decoration-none"
            :class="$style.termsPrivacy"
            >Terms</a
          >
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.termsRow {
  position: absolute;
  right: 0;
  bottom: 10px;
  width: 100%;
}

.termsPrivacy {
  font-size: 20px;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 1px;
}

.innerContainer {
  max-width: 400px;
  margin-bottom: 160px;
}

.title {
  margin-bottom: 25px !important;
  font-size: 32px;
  font-weight: 400;
  line-height: 48px;
  letter-spacing: 1px;
}

.subtitle {
  margin-bottom: 25px !important;
  font-size: 13px;
  font-weight: 500;
  line-height: 22px;
  text-align: center;
  letter-spacing: 0.25px;
}

.email {
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.75px;
  border-radius: 16px !important;
}

.hrButton {
  font-size: 15px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.75px;
  border-radius: 12px !important;
}

.passTooltip {
  left: -320px !important;
}

.hrRouterLinkText {
  font-family: $system-default-font-family;
  font-size: 20px;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 1px;
}
</style>
